import * as systemUtils from "./utils/systemUtils";

// Custom keys added to the model (they are not present in the model file).
const tagNodeID = "id";
const tagNodeUUID = "uuid";
const tagNodeName = "name";
const tagNodeType = "type";
const tagNodeTimeCreate = "timeCreate";
const tagNodeTimeUpdate = "timeUpdate";
const tagParameterReferences = "references";

// Keys that accept arrays.
const tagParametersGlobal = "parameters";
const tagParameters = "parameters";
const tagFunctions = "functions";
const tagChoices = "choices";
const tagConditions = "conditions";

// Keys that accept primitives.
const tagParameterOutput = "output";
const tagParameterTarget = "target";
const tagChoiceValue = "value";
const tagConditionExpression = "expression";
const tagNodeLabel = "labels";

// Metadata.
const tagMetaComment = "comment";

//------------------------------------------------------------------------
// GETTERS - TAGS
//------------------------------------------------------------------------

export const getTagParametersGlobal = () => {
  return tagParametersGlobal;
};

export const getTagFunctions = () => {
  return tagFunctions;
};

export const getTagParameters = () => {
  return tagParameters;
};

export const getTagParameterOutput = () => {
  return tagParameterOutput;
};

export const getTagParameterTarget = () => {
  return tagParameterTarget;
};

export const getTagChoices = () => {
  return tagChoices;
};

export const getTagChoiceValue = () => {
  return tagChoiceValue;
};

export const getTagConditions = () => {
  return tagConditions;
};

export const getTagConditionExpressions = () => {
  return tagConditionExpression;
};

export const getTagLabel = () => {
  return tagNodeLabel;
};

export const getTagComment = () => {
  return tagMetaComment;
};

//------------------------------------------------------------------------
// GETTERS/SETTERS - ALL NODES
//------------------------------------------------------------------------

export const getID = (node) => {
  return get(node, tagNodeID);
};

export const setID = (node, id) => {
  set(node, tagNodeID, id);
};

export const getUUID = (node) => {
  return get(node, tagNodeUUID);
};

export const setUUID = (node, uuid) => {
  set(node, tagNodeUUID, uuid);
};

export const getName = (node) => {
  return get(node, tagNodeName);
};

export const setName = (node, name) => {
  set(node, tagNodeName, name);
};

export const getType = (node) => {
  return get(node, tagNodeType);
};

export const setType = (node, type) => {
  set(node, tagNodeType, type);
};

export const getTimeCreate = (node) => {
  return get(node, tagNodeTimeCreate, 0);
};

export const setTimeCreate = (node, time) => {
  set(node, tagNodeTimeCreate, time);
};

export const getTimeUpdate = (node) => {
  return get(node, tagNodeTimeUpdate, 0);
};

export const setTimeUpdate = (node, time) => {
  set(node, tagNodeTimeUpdate, time);
};

export const getLabel = (node) => {
  return getRefArray(node, tagNodeLabel);
};

export const setLabel = (node, label) => {
  setRefArray(node, tagNodeLabel, label);
};

export const getComment = (node) => {
  return getMeta(node, tagMetaComment);
};

export const setComment = (node, comment) => {
  setMeta(node, tagMetaComment, comment);
};

//------------------------------------------------------------------------
// GETTERS/SETTERS - SPECIALIZED NODES
//------------------------------------------------------------------------

export const getFunctions = (root) => {
  return getRefArray(root, tagFunctions);
};

export const setFunctions = (root, functions) => {
  setRefArray(root, tagFunctions, functions);
};

export const getIdTarget = (parameter) => {
  return get(parameter, tagParameterTarget);
};

export const setIdTarget = (parameter, idTarget) => {
  set(parameter, tagParameterTarget, idTarget);
};

export const getIdReferences = (parameter) => {
  return getRefArray(parameter, tagParameterReferences);
};

export const setIdReferences = (parameter, reference) => {
  setRefArray(parameter, tagParameterReferences, reference);
};

export const getValueDefault = (parameter) => {
  return get(parameter, tagParameterOutput);
};

export const setValueDefault = (parameter, valueDefault) => {
  set(parameter, tagParameterOutput, valueDefault);
};

export const getValue = (choice) => {
  return get(choice, tagChoiceValue);
};

export const setValue = (choice, value) => {
  set(choice, tagChoiceValue, value);
};

export const getParameters = (container) => {
  return getRefArray(container, tagParameters);
};

export const setParameters = (container, parameters) => {
  setRefArray(container, tagParameters, parameters);
};

export const getChoices = (container) => {
  return getRefArray(container, tagChoices);
};

export const setChoices = (container, choices) => {
  setRefArray(container, tagChoices, choices);
};

export const getConditions = (container) => {
  return getRefArray(container, tagConditions);
};

export const setConditions = (container, conditions) => {
  setRefArray(container, tagConditions, conditions);
};

export const getExpression = (condition) => {
  return get(condition, tagConditionExpression);
};

export const setExpression = (condition, expressions) => {
  set(condition, tagConditionExpression, expressions);
};

//------------------------------------------------------------------------

const get = (node, type, def = null) => {
  return node?.[type] ?? def;
};

const set = (node, type, value) => {
  if (value === null) {
    delete node[type];

    return;
  }

  node[type] = value;
};

const getMeta = (node, type, def = null) => {
  return node?.metadata?.[type] ?? def;
}

const setMeta = (node, type, value) => {

  if (!node.metadata) {
    node.metadata = {};
  }

  node.metadata[type] = value;
}

//------------------------------------------------------------------------

const getRefArray = (node, type, def = []) => {
  const results = node?.[type];

  if (systemUtils.isArray(results)) {
    return [...results];
  }

  return def;
};

const setRefArray = (node, type, value) => {
  if (value === null) {
    delete node[type];

    return;
  }

  if (!systemUtils.isArray(value)) {
    throw new Error("The value must be an array.");
  }

  if (value.length === 0) {
    delete node[type];
  } else {
    if (!node[type] || !systemUtils.isArray(node[type])) {
      node[type] = value;
    } else {
      node[type].splice(0, node[type].length, ...value);
    }
  }
};

//------------------------------------------------------------------------

export const createNode = () => {
  return {};
};
